import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowUpRight, ChevronDown, Globe, Heart } from 'lucide-react';
import { FaDiscord, FaFacebookF, FaInstagram, FaTiktok, FaXTwitter } from 'react-icons/fa6';
import { useAuth } from '../contexts/AuthContext';
import { SOCIAL_LINKS } from '../config/socialLinks';
import { useLanguage } from '../contexts/LanguageContext';
import './Footer.css';

const columns = [
  {
    title: 'GoPlay',
    links: [
      ['Accueil', '/'],
      ['Carte', '/map'],
      ['Événements', '/events'],
      ['Challenges', '/challenges'],
      ['Créer', '/create-event'],
    ],
  },
  {
    title: 'Communauté',
    links: [
      ['Trouver un match', '/matches'],
      ['Joueurs', '/players'],
      ['Amis', '/friends'],
      ['Classements', '/rankings'],
    ],
  },
  {
    title: 'Support',
    links: [
      ['Centre d’aide', '/help'],
      ['Nous contacter', '/contact'],
      ['Signaler un problème', '/report'],
      ['FAQ', '/faq'],
    ],
  },
  {
    title: 'Legal',
    links: [
      ["Conditions d'utilisation", '/terms'],
      ['Politique de confidentialité', '/privacy'],
      ['Cookies', '/cookies'],
    ],
  },
];

const socials = [
  ['Instagram', SOCIAL_LINKS.instagram, FaInstagram],
  ['TikTok', SOCIAL_LINKS.tiktok, FaTiktok],
  ['Facebook', SOCIAL_LINKS.facebook, FaFacebookF],
  ['X / Twitter', SOCIAL_LINKS.x, FaXTwitter],
  ['Discord', SOCIAL_LINKS.discord, FaDiscord],
];

function FooterColumn({ column, isOpen, onToggle }) {
  const location = useLocation();

  return (
    <div className={`footer-column ${isOpen ? 'is-open' : ''}`}>
      <button className="footer-column-toggle" type="button" aria-expanded={isOpen} onClick={onToggle}>
        <span>{column.title}</span>
        <ChevronDown aria-hidden="true" size={17} />
      </button>
      <ul className="footer-link-list">
        {column.links.map(([label, path]) => (
          <li key={label}>
            {path.startsWith('/') ? <Link className={location.pathname === path || (path !== '/' && location.pathname.startsWith(`${path}/`)) ? 'is-active' : ''} to={path}>{label}</Link> : <a href={path}>{label}</a>}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Footer() {
  const { currentUser } = useAuth();
  const { language, setLanguage, supportedLanguages } = useLanguage();
  const [openColumn, setOpenColumn] = useState(null);
  const ctaPath = currentUser ? '/events' : '/login';

  return (
    <footer className="site-footer">
      <div className="site-footer-inner">
        <div className="footer-main-grid">
          <div className="footer-brand-block">
            <Link to="/" className="footer-brand" aria-label="GoPlay, accueil">
              <span className="footer-logo-wrap"><img src="/logo.png" alt="" /></span>
              <span>GoPlay</span>
            </Link>
            <p className="footer-tagline">Le sport. Les rencontres. L'expérience.</p>
            <p className="footer-description">Découvrez des matchs, participez à des événements et connectez-vous avec une communauté de passionnés de sport.</p>
            <div className="footer-social-block">
              <span className="footer-eyebrow">Suivez GoPlay</span>
              <div className="footer-socials">
                {socials.map(([label, href, Icon]) => (
                  <a key={label} className="footer-social-link" href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
                    <Icon aria-hidden="true" size={17} />
                  </a>
                ))}
              </div>
            </div>
          </div>

          <nav className="footer-navigation" aria-label="Navigation du pied de page">
            {columns.map((column, index) => (
              <FooterColumn
                key={column.title}
                column={column}
                isOpen={openColumn === index}
                onToggle={() => setOpenColumn(openColumn === index ? null : index)}
              />
            ))}
          </nav>

          <div className="footer-cta">
            <span className="footer-cta-kicker"><span className="footer-live-dot" /> La prochaine rencontre commence ici</span>
            <h2>Prêt à jouer&nbsp;?</h2>
            <p>Rejoignez la communauté GoPlay et trouvez votre prochain match.</p>
            <Link className="footer-cta-button" to={ctaPath}>Commencer à jouer <ArrowUpRight aria-hidden="true" size={17} /></Link>
          </div>
        </div>

        <div className="footer-bottom-bar">
          <span>© 2026 GoPlay. Tous droits réservés.</span>
          <span className="footer-made-with">Fait avec <Heart aria-hidden="true" size={14} fill="currentColor" /> pour les passionnés de sport.</span>
          <label className="footer-language" aria-label="Choisir la langue">
            <Globe aria-hidden="true" size={15} />
            <select value={language} onChange={(event) => setLanguage(event.target.value)} aria-label="Langue">
              {supportedLanguages.map(({ code, label }) => <option key={code} value={code}>{label}</option>)}
            </select>
            <ChevronDown aria-hidden="true" size={14} />
          </label>
        </div>
      </div>
    </footer>
  );
}